import { useMemo, useState, useCallback } from 'react';

import { useLazyGetRecordHistoryQuery } from '@app/api/Records/api';
import {
  setHasChanges,
  setIsLoading,
} from '@app/components/RecordModal/model/recordModalSlice';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useAppDispatch, useAppSelector } from '@app/store';
import type { NActionsHistory } from '@app/types';

const SERVICE_PROPERTY = 'Услуга';

// Backend logs an added/removed service as a single-sided change
// (ValueOld or ValueNew is null), which renders as a dangling arrow.
const formatServiceItem = (
  item: NActionsHistory.ModelActionHistoryItem
): NActionsHistory.ModelActionHistoryItem => {
  if (item.Property !== SERVICE_PROPERTY) {
    return item;
  }
  if (item.ValueNew && !item.ValueOld) {
    return {
      ...item,
      Icon: 'fa-solid fa-circle-plus',
      Property: 'Добавлена услуга',
    };
  }
  if (item.ValueOld && !item.ValueNew) {
    return {
      ...item,
      Icon: 'fa-solid fa-circle-minus',
      Property: 'Удалена услуга',
      ValueNew: item.ValueOld,
      ValueOld: null,
    };
  }
  return item;
};

const formatActions = (
  actions: NActionsHistory.Action[]
): NActionsHistory.Action[] =>
  actions.map((action) => ({
    ...action,
    ActionItems: action.ActionItems?.map(formatServiceItem) ?? null,
  }));

export const useRecordHistory = () => {
  const dispatch = useAppDispatch();
  const { record, isOpen } = useAppSelector(selectRecordModal);
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [getRecordHistory] = useLazyGetRecordHistoryQuery();

  const recordId = useMemo(() => record?.Id, [record?.Id]);

  const [actions, setActions] = useState<NActionsHistory.Action[]>([]);

  const fetchRecordHistory = useCallback(async () => {
    if (!isOpen) {
      return;
    }
    dispatch(setHasChanges(false));
    dispatch(setIsLoading(true));
    if (!recordId) {
      showNotification('Запись не найдена', FRONT_WARNING);
      dispatch(setIsLoading(true));
      return;
    }

    try {
      const result = await getRecordHistory({
        ActiveBranchId: activeBranchId,
        recordId,
      }).unwrap();
      setActions(formatActions(result));
    } catch (error) {
      showNotification(error, WARNING);
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [
    activeBranchId,
    recordId,
    showNotification,
    dispatch,
    isOpen,
    getRecordHistory,
  ]);

  return { actions, fetchRecordHistory };
};
